import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { getReadingForDate } from '../data/schedule';
import AudioPlayer from '../components/AudioPlayer';
import VerseRef from '../components/VerseRef';
import CommentsSection from '../components/CommentsSection';
import DailyBread from '../components/DailyBread';
import LangSelect from '../components/LangSelect';
import { useUser } from '../context/UserContext';
import { pick, readingText, annotationsText } from '../i18n';
import { PopupContext, MarkerSup } from '../bible/VerseWithMarkers';
import { useRefPopups, RefPopupLayer } from '../bible/RefPopups';
import { fetchChapterRefs, refsLangFor } from '../bible/chapterData';
import { parseReadingLines } from '../bible/readingLines';
import { AnnotatedVerse } from '../annotations/AnnotatedVerse';
import { AnnotationLayer } from '../annotations/AnnotationLayer';
import { compareVerses } from '../annotations/anchors';
import { portionBounds, timingLangFor } from '../audio/timings';
import { lineOwners } from '../audio/follow';
import { useFollowVerse } from '../audio/useFollowVerse';
import FollowAudioPill from '../components/FollowAudioPill';
import { NT_SET } from '../utils/bibleBooks';
import { usePageTurn, TurnPage } from '../pageturn/usePageTurn';
import { SkeletonLines } from '../bible/ChapterPreview';

// The readings repeat every year, stored under 2026; Feb 29 reads Feb 28.
function queryDateOf(date) {
  const q = date.replace(/^\d{4}/, '2026');
  return q.endsWith('-02-29') ? '2026-02-28' : q;
}

// The local calendar date `delta` days from `date` (both YYYY-MM-DD).
function shiftDate(date, delta) {
  const d = new Date(date + 'T12:00:00');
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString('en-CA');
}

// Each day's verses row, kept for the visit: the days either side are fetched
// ahead so a page turn slides in real text, and turning back is free.
const VERSE_COLS = 'nt_title,ot_title,nt_text,ot_text,nt_text_zh,ot_text_zh,nt_text_sc,ot_text_sc,nt_text_es,ot_text_es,nt_audio,ot_audio,nt_audio_zh,ot_audio_zh';
const dayRows = new Map();
const dayRequests = new Map();
function fetchDayRow(queryDate) {
  if (dayRows.has(queryDate)) return Promise.resolve(dayRows.get(queryDate));
  if (!dayRequests.has(queryDate)) {
    dayRequests.set(queryDate, supabase.from('verses').select(VERSE_COLS).eq('date', queryDate).single()
      .then(({ data }) => {
        if (data) {
          if (dayRows.size >= 40) dayRows.clear();   // a visit never needs more
          dayRows.set(queryDate, data);
        }
        return data || null;
      }, () => null)
      .finally(() => dayRequests.delete(queryDate)));
  }
  return dayRequests.get(queryDate);
}

// How far down the window the pinned bars reach (the Daily Bread card pins
// under the controls), so a turning day is drawn from just below them.
function readingTopInset() {
  let bottom = 0;
  for (const sel of ['.navbar', '.sticky-banner', '.reading-sticky-controls', '.daily-bread']) {
    const el = document.querySelector(sel);
    if (el) bottom = Math.max(bottom, el.getBoundingClientRect().bottom);
  }
  return bottom;
}

// A line that belongs to the verse above it (the second line of a poetic
// verse). With refs on, that verse is drawn whole from its marked text, so the
// line is left out rather than shown twice.
const CONTINUATION = 'continuation';

const NO_REFS = { map: {}, texts: {}, headings: {} };

// The bars pinned above the cards, which the playing verse is kept clear of.
const FOLLOW_HEADERS = ['.navbar', '.sticky-banner', '.reading-sticky-controls', '.daily-bread > summary'];

// What a verse line says about itself, for lighting and following the verse
// being read (useFollowVerse finds lines by these, in both columns at once).
function verseAttrs(v, lang) {
  if (!v) return null;
  return { 'data-book': v.abbr, 'data-chapter': v.chapter, 'data-verse': v.verse, 'data-lang': lang };
}

export default function Reading({ lang = 'en' }) {
  // The cache is for turning days within one visit; leaving the page drops it,
  // so an edit made elsewhere (e.g. an admin correcting a day) shows on return.
  React.useEffect(() => () => dayRows.clear(), []);
  const { user } = useUser();
  const name = user?.name || '';
  const userId = user?.id || null;

  // ── Refs mode (cross-references and footnotes) ──────────────────────────
  // Admins only for now; this becomes `true` for everyone once refs are opened
  // up. The flag only decides what is shown — the refs tables are publicly
  // readable anyway — so trusting the client-side isAdmin here is fine.
  const canSeeRefs = !!user?.isAdmin;
  const [showRefs, setShowRefs] = React.useState(false);
  // `${book}_${chapter}_${lang}` → { map, texts, headings } (see fetchChapterRefs)
  const [refsData, setRefsData] = React.useState({});
  const popups = useRefPopups();
  const refsOn = canSeeRefs && showRefs;
  // Holds the reading cards, where selecting verse text opens the highlight
  // and note toolbar (src/annotations/).
  const annoRootRef = React.useRef(null);
  // Signing out turns the markers off rather than leaving them with no button.
  React.useEffect(() => { if (!canSeeRefs) setShowRefs(false); }, [canSeeRefs]);

  const [searchParams, setSearchParams] = useSearchParams();
  // Local date (device timezone), not UTC — toISOString() rolls over to tomorrow
  // late in the evening for timezones behind UTC. en-CA formats as YYYY-MM-DD.
  const todayStr = new Date().toLocaleDateString('en-CA');
  const currentDate = searchParams.get('date') || todayStr;
  const queryDate = queryDateOf(currentDate);
  // Comments keep the real year: the section already gathers every year's
  // comments for this month-day, and the year is what lets it label them.
  // Feb 29 shares Feb 28's discussion, as it shares its reading.
  const commentDate = currentDate.endsWith('-02-29')
    ? currentDate.slice(0, 4) + '-02-28'
    : currentDate;

  const dayLabel = (date, opts = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) =>
    new Date(date + 'T12:00:00').toLocaleDateString(
      lang === 'zh' || lang === 'sc' ? 'zh-TW' : lang === 'es' ? 'es-ES' : 'en-US', opts);
  const dateLabel = dayLabel(currentDate);
  const startOfYear = new Date(new Date(currentDate).getFullYear(), 0, 0);
  const dayOfYear = Math.floor((new Date(currentDate + 'T12:00:00') - startOfYear) / 86400000);
  // Counted rather than fixed at 365, so a leap year reads "Day 231 of 366".
  const year = Number(currentDate.slice(0, 4));
  const daysInYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
  const reading = getReadingForDate(currentDate);

  const [ntDone, setNtDone] = React.useState(false);
  const [otDone, setOtDone] = React.useState(false);
  const [saving, setSaving] = React.useState('');
  const [toast, setToast] = React.useState('');
  const toastTimer = React.useRef(null);
  React.useEffect(() => () => clearTimeout(toastTimer.current), []);
  const [todayReaders, setTodayReaders] = React.useState([]);
  const [bannerItems, setBannerItems] = React.useState([]);
  const [verses, setVerses] = React.useState(null);
  // The date `verses` was loaded for, so a page turn knows when the day it
  // turned to is really on screen.
  const [versesDate, setVersesDate] = React.useState(null);
  const loadingFor = React.useRef(null);
  const [showNT, setShowNT] = React.useState(true);
  const [showOT, setShowOT] = React.useState(true);
  const [fontSize, setFontSize] = React.useState(() => parseInt(localStorage.getItem('readingFontSize'), 10) || 18);
  const [navbarHeight, setNavbarHeight] = React.useState(54);
  const [bannerHeight, setBannerHeight] = React.useState(0);
  const [controlsHeight, setControlsHeight] = React.useState(0);
  const [parallelMode, setParallelMode] = React.useState(() =>
    localStorage.getItem('parallelMode') === 'true'
  );
  const [parallelLangA, setParallelLangA] = React.useState(() =>
    localStorage.getItem('parallelLangA') || lang
  );
  const [parallelLangB, setParallelLangB] = React.useState(() => {
    const storedB = localStorage.getItem('parallelLangB');
    const initA   = localStorage.getItem('parallelLangA') || lang;
    // Ensure B is different from A at startup
    if (storedB && storedB !== initA) return storedB;
    return initA === 'zh' || initA === 'sc' || initA === 'es' ? 'en' : 'zh';
  });

  React.useEffect(() => {
    const navbar = document.querySelector('.navbar');
    if (!navbar) return;
    const update = () => setNavbarHeight(navbar.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(navbar);
    return () => observer.disconnect();
  }, []);

  // The NT/OT audio players by `${portion}:${lang}`, for "listen from here".
  const playersRef = React.useRef({});
  const playerRef = key => el => {
    if (el) playersRef.current[key] = el;
    else delete playersRef.current[key];
  };
  // The verse being read: lit in the cards, and followed down the page while
  // the recording plays. Each player reports under its playersRef key.
  const follow = useFollowVerse(annoRootRef, FOLLOW_HEADERS);
  const followProps = key => ({
    onVerseChange: v => follow.onVerse(key, v),
    onPlayingChange: playing => follow.onPlaying(key, playing),
  });
  // Timings load inside each player, after it is drawn; this redraws the verse
  // references once they can offer "play from verse".
  const [, setTimingsTick] = React.useState(0);
  const onTimings = React.useCallback(() => setTimingsTick(n => n + 1), []);
  const ta = pick(annotationsText, lang);
  // A reference that plays its verse: the player for that verse's testament in
  // `playLang` (the column's language in parallel mode) when it has the verse
  // timed, else null and the reference stays plain text.
  function refPlay(parsed, playLang) {
    if (!parsed) return null;
    const player = playersRef.current[`${NT_SET.has(parsed.abbr) ? 'nt' : 'ot'}:${playLang}`];
    if (!player?.canPlayFrom(parsed.abbr, parsed.chapter, parsed.verse)) return null;
    return () => player.playFrom(parsed.abbr, parsed.chapter, parsed.verse);
  }

  // Measure the marquee banner so the sticky control bar parks right below it.
  React.useEffect(() => {
    const banner = document.querySelector('.sticky-banner');
    if (!banner) { setBannerHeight(0); return; }
    const update = () => setBannerHeight(banner.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(banner);
    return () => observer.disconnect();
  }, [bannerItems.length]);

  const controlsTop = navbarHeight + bannerHeight;

  // Measure the control bar too, so Today's Reading parks directly beneath it.
  // Its height changes when parallel mode adds the two language selects.
  React.useEffect(() => {
    const controls = document.querySelector('.reading-sticky-controls');
    if (!controls) { setControlsHeight(0); return; }
    const update = () => setControlsHeight(controls.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(controls);
    return () => observer.disconnect();
  }, [parallelMode]);

  const dailyBreadTop = controlsTop + controlsHeight;


  React.useEffect(() => { localStorage.setItem('readingFontSize', String(fontSize)); }, [fontSize]);

  React.useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    setNtDone(false); setOtDone(false);
    setShowNT(true); setShowOT(true);
    setVerses(null); setTodayReaders([]); setBannerItems([]);
    loadVerses();
    if (name) loadReaders(name);
  }, [currentDate, name]);

  function timeAgo(ts) {
    const m = Math.floor((Date.now() - new Date(ts)) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  async function loadVerses() {
    // Always fetch all language columns so switching parallel langs never shows a fallback.
    // The daily verse row is tiny (~20-30 verses × 4 langs) — fetching everything upfront
    // is cheaper than a re-fetch every time the user changes language in parallel mode.
    // A day fetched ahead (the ones either side) is drawn straight away.
    const forDate = currentDate;
    loadingFor.current = forDate;
    const cached = dayRows.get(queryDate);
    if (cached) { setVerses(cached); setVersesDate(forDate); return; }
    const data = await fetchDayRow(queryDate);
    // Turning through days quickly: an older day's answer arriving late must
    // not replace the day now showing.
    if (loadingFor.current !== forDate) return;
    if (data) setVerses(data);
    setVersesDate(forDate);
  }

  async function loadReaders(n) {
    const { data } = await supabase.from('checkins')
      .select('name,portion,created_at,user_id').eq('date', currentDate)
      .order('created_at', { ascending: true });
    if (!data) return;
    if (n) {
      // Match on the account when both sides have one; rows written before the
      // migration only carry a name, so that stays the fallback.
      const my = data.filter(r => (userId && r.user_id)
        ? r.user_id === userId
        : r.name.toLowerCase() === n.toLowerCase());
      if (my.some(r => r.portion === 'NT')) setNtDone(true);
      if (my.some(r => r.portion === 'OT')) setOtDone(true);
    }
    const map = {};
    data.forEach(r => {
      const k = r.user_id || r.name.toLowerCase();
      if (!map[k]) map[k] = { name: r.name, nt: false, ot: false };
      if (r.portion === 'NT') map[k].nt = true;
      if (r.portion === 'OT') map[k].ot = true;
    });
    setTodayReaders(Object.values(map));
    const recent = [...data].reverse().slice(0, 8);
    setBannerItems(recent.map(r => ({ name: r.name, portion: r.portion, time: timeAgo(r.created_at) })));
  }

  async function handleCheckin(portion, done, setDone) {
    if (done || saving || !name) return;
    setSaving(portion);
    // Look for an existing check-in by account first, so a renamed reader is
    // still recognised, then by name for rows that predate the account id.
    let ex = null;
    if (userId) {
      ({ data: ex } = await supabase.from('checkins').select('id')
        .eq('date', currentDate).eq('portion', portion).eq('user_id', userId));
    }
    if (!ex?.length) {
      ({ data: ex } = await supabase.from('checkins').select('id')
        .eq('date', currentDate).eq('portion', portion).ilike('name', name));
    }
    if (ex && ex.length > 0) { setDone(true); setSaving(''); return false; }
    // Both are written: the id is the identity, the name keeps anything still
    // reading by name working until that is retired.
    const { error } = await supabase.from('checkins')
      .insert({ name, user_id: userId, date: currentDate, portion });
    if (!error) { setDone(true); loadReaders(name); }
    setSaving('');
    return !error;
  }

  // Listening through the last chapter's audio counts as finishing that portion.
  async function handleAudioFinished(portion) {
    const recorded = portion === 'NT'
      ? await handleCheckin('NT', ntDone, setNtDone)
      : await handleCheckin('OT', otDone, setOtDone);
    if (!recorded) return;
    clearTimeout(toastTimer.current);
    setToast(portion === 'NT' ? t.autoFinishedNT : t.autoFinishedOT);
    toastTimer.current = setTimeout(() => setToast(''), 4000);
  }

  // `replace` keeps a run of days from a held arrow key out of the back button.
  function changeDate(delta, replace = false) {
    const nd = shiftDate(currentDate, delta); // local date, matches todayStr
    setSearchParams(nd === todayStr ? {} : { date: nd }, { replace });
  }

  // `row` is the day's verses row: today's by default, or a neighbouring
  // day's for the page sliding in during a turn.
  function getTextForLang(langCode, portion, fallback = true, row = verses) {
    if (!row) return null;
    const base = portion === 'nt' ? row.nt_text : row.ot_text;
    if (langCode === 'es') {
      const t2 = portion === 'nt' ? row.nt_text_es : row.ot_text_es;
      return t2 || (fallback ? base : null);
    }
    if (langCode === 'zh') {
      const t2 = portion === 'nt' ? row.nt_text_zh : row.ot_text_zh;
      return t2 || (fallback ? base : null);
    }
    if (langCode === 'sc') {
      const t2 = portion === 'nt' ? row.nt_text_sc : row.ot_text_sc;
      return t2 || (fallback ? base : null);
    }
    return base;
  }
  function getNtText() { return getTextForLang(lang, 'nt'); }
  function getOtText() { return getTextForLang(lang, 'ot'); }
  function getNtAudio() { return getAudioForLang(lang, 'nt'); }
  function getOtAudio() { return getAudioForLang(lang, 'ot'); }

  function getTitle(title, langCode = lang) {
    if (!title || langCode === 'en') return title;
    let normalized = title;
    Object.entries(ABBREV).sort((a,b) => b[0].length - a[0].length).forEach(([abbr, full]) => {
      const escaped = abbr.replace(/\./g, '\\.');
      const endsWithWordChar = /\w/.test(abbr[abbr.length - 1]);
      const pattern = '\\b' + escaped + (endsWithWordChar ? '\\b' : '');
      normalized = normalized.replace(new RegExp(pattern, 'g'), full);
    });
    if (langCode === 'es') {
      let r = normalized
        .replace('New Testament', 'Nuevo Testamento')
        .replace('Old Testament', 'Antiguo Testamento');
      // Book names too, or the heading reads "Antiguo Testamento - Psalms 31:1~33:22"
      Object.entries(ES_MAP).sort((a, b) => b[0].length - a[0].length).forEach(([full, abbr]) => {
        r = r.replace(new RegExp(full, 'g'), abbr);
      });
      return r;
    }
    const isZh = langCode === 'zh' || langCode === 'sc';
    if (isZh) {
      const cmap = langCode === 'sc' ? SC_MAP : ZH_MAP;
      let r = normalized
        .replace('New Testament', langCode === 'sc' ? '新约' : '新約')
        .replace('Old Testament', langCode === 'sc' ? '旧约' : '舊約');
      r = r.replace(/\b1 King\b/g, cmap['1 Kings'] || '王上')
           .replace(/\b2 King\b/g, cmap['2 Kings'] || '王下');
      Object.entries(cmap).sort((a,b) => b[0].length - a[0].length).forEach(([full, char]) => {
        r = r.replace(new RegExp(full, 'g'), char);
      });
      return r;
    }
    return title;
  }

  // ── Refs: tie each displayed line to its verse in bible_chapters ─────────
  // The text a card shows, and the language it is really in: a missing
  // translation falls back to English in single-language mode.
  function textLangOf(langCode, portion) {
    return getTextForLang(langCode, portion, false) ? langCode : 'en';
  }

  // Every (text, language) the cards are showing right now, for refs.
  const refSources = [];
  if (refsOn && verses) {
    for (const portion of ['nt', 'ot']) {
      const title = verses[`${portion}_title`];
      if (parallelMode) {
        for (const l of [parallelLangA, parallelLangB]) {
          refSources.push({ text: getTextForLang(l, portion, false), title, lang: l });
        }
      } else {
        refSources.push({ text: getTextForLang(lang, portion), title, lang: textLangOf(lang, portion) });
      }
    }
  }
  // The chapters those lines come from — a portion can run across chapters
  // and even books. Joined into one string so the fetch below reruns only when
  // the set of chapters actually changes.
  const wantedRefKey = [...new Set(refSources.flatMap(({ text, title, lang: l }) => {
    const rl = refsLangFor(l);
    if (!rl || !text) return [];
    return parseReadingLines(text, title).filter(Boolean).map(p => `${p.abbr}_${p.chapter}_${rl}`);
  }))].sort().join(',');

  React.useEffect(() => {
    if (!wantedRefKey) return;
    let cancelled = false;
    Promise.all(wantedRefKey.split(',').map(async key => {
      const [abbr, chapter, l] = key.split('_');
      // A chapter that cannot be loaded just stays plain text.
      try { return [key, await fetchChapterRefs(abbr, Number(chapter), l)]; }
      catch { return [key, NO_REFS]; }
    })).then(entries => {
      if (!cancelled) setRefsData(prev => ({ ...prev, ...Object.fromEntries(entries) }));
    });
    return () => { cancelled = true; };
  }, [wantedRefKey]);

  // One entry per line of `text`: null to draw the line as usual, CONTINUATION
  // to leave it out, or the verse's marked text and refs to draw it with
  // markers. Returns null (draw everything as usual) when refs are off or the
  // language has none.
  function annotateLines(text, title, textLang) {
    const rl = refsLangFor(textLang);
    if (!refsOn || !text || !rl) return null;
    const lines = text.split('\n');
    const parsed = parseReadingLines(text, title);
    let afterMarked = false;
    return parsed.map((p, i) => {
      if (p) {
        const data = refsData[`${p.abbr}_${p.chapter}_${rl}`];
        const marked = data?.texts[p.verse];
        afterMarked = !!marked;
        return marked ? { ...p, marked, refsMap: data.map, lang: rl } : null;
      }
      const line = lines[i].trim();
      if (!afterMarked || !line) return null;
      // An unreferenced line is either the rest of the verse above or, in the
      // Psalms, a heading over the verse below; the stored headings tell them apart.
      const next = parsed.slice(i + 1).find(Boolean);
      const heading = next && refsData[`${next.abbr}_${next.chapter}_${rl}`]?.headings[next.verse];
      if (heading?.some(h => h.trim() === line)) { afterMarked = false; return null; }
      return CONTINUATION;
    });
  }

  // `parsed` is the line's verse reference from parseReadingLines and
  // `textLang` the language the line is really in; together they anchor the
  // reader's highlights and notes, which are shared with The Bible page.
  // Lines that are not a verse of their own (Psalm headings, the second line
  // of a poetic verse) are drawn plain and cannot be marked: The Bible stores
  // only each verse's first line, so there is nothing there to anchor to.
  // `owner` is the verse a line without a reference of its own continues
  // (lineOwners), so every line of a poetic verse lights up while it is read.
  function formatVerseLine(line, key, note = null, parsed = null, textLang = lang, playLang = lang, owner = null) {
    if (note === CONTINUATION) return null;
    if (note) return (
      <p key={key} className="verse-line" style={{ fontSize: fontSize + 'px' }} {...verseAttrs(note, textLang)}>
        <VerseRef text={note.prefix} label={ta.playFromVerse(note.verse)} onPlay={refPlay(note, playLang)} />
        <span className="verse-body">{' '}
          <AnnotatedVerse book={note.abbr} chapter={note.chapter} verse={note.verse} lang={textLang}
            text={note.body} marked={note.marked}
            renderMarker={(marker, k) => (
              <MarkerSup key={k} marker={marker} verseNum={note.verse} refsMap={note.refsMap}
                verseLang={note.lang} contextBook={note.abbr} contextChapter={note.chapter} anchored />
            )} />
        </span>
      </p>
    );
    if (!line) return null;
    if (parsed) return (
      <p key={key} className="verse-line" style={{ fontSize: fontSize + 'px' }} {...verseAttrs(parsed, textLang)}>
        <VerseRef text={parsed.prefix} label={ta.playFromVerse(parsed.verse)} onPlay={refPlay(parsed, playLang)} />
        <span className="verse-body">{' '}
          <AnnotatedVerse book={parsed.abbr} chapter={parsed.chapter} verse={parsed.verse}
            lang={textLang} text={parsed.body} />
        </span>
      </p>
    );
    const mEn = line.match(/^([A-Z][a-z]+ \d+ :\d+)\s+(.*)/);
    if (mEn) return (
      <p key={key} className="verse-line" style={{ fontSize: fontSize + 'px' }}>
        <span className="verse-ref">{mEn[1]}</span><span className="verse-body"> {mEn[2]}</span>
      </p>
    );
    // Leading "\d " covers numbered books in the Spanish abbreviations: "1 Co. 3:1", "2 S. 7:1"
    const mOther = line.match(/^((?:\d\s+)?[\w\u4e00-\u9fff]+\.?\s*\d+:\d+)\s+(.*)/);
    if (mOther) return (
      <p key={key} className="verse-line" style={{ fontSize: fontSize + 'px' }}>
        <span className="verse-ref">{mOther[1]}</span><span className="verse-body"> {mOther[2]}</span>
      </p>
    );
    return line ? <p key={key} className="verse-line" style={{ fontSize: fontSize + 'px' }} {...verseAttrs(owner, textLang)}>{line}</p> : null;
  }

  function formatVerses(text, notes, title, textLang) {
    if (!text) return null;
    const parsed = parseReadingLines(text, title);
    const lines = text.split('\n');
    const owners = lineOwners(lines, parsed);
    return lines.map((line, i) => formatVerseLine(line, i, notes?.[i], parsed[i], textLang, lang, owners[i]));
  }

  // Each column carries its own refs in its own language, as on The Bible
  // page; a column in a language without refs (Spanish) stays plain. Rows
  // still pair lines by position, exactly as without refs. Marks work in
  // either column, each in that column's language.
  function formatVersesParallel(textA, textB, langA = 'en', langB = 'zh', notesA = null, notesB = null, title = '') {
    if (!textA && !textB) return null;
    // If one column is missing, use the other as driver and show placeholder
    const driverText    = textA || textB;
    const otherText     = textA ? textB : null;
    const leftIsDriver  = !!textA;
    const linesDriver   = driverText.split('\n');
    const linesOther    = otherText ? otherText.split('\n') : null;
    const parsedA = textA ? parseReadingLines(textA, title) : [];
    const parsedB = textB ? parseReadingLines(textB, title) : [];
    const ownersA = textA ? lineOwners(textA.split('\n'), parsedA) : [];
    const ownersB = textB ? lineOwners(textB.split('\n'), parsedB) : [];
    return linesDriver.map((lineDriver, i) => {
      const leftLine  = leftIsDriver ? lineDriver : (linesOther ? linesOther[i] || '' : null);
      const rightLine = leftIsDriver ? (linesOther ? linesOther[i] || '' : null) : lineDriver;
      // Line i of each column is that column's own line i, whichever drives.
      const noteA = notesA?.[i] ?? null;
      const noteB = notesB?.[i] ?? null;
      if (noteA === CONTINUATION && noteB === CONTINUATION) return null;
      return (
        <div key={i} className="parallel-row">
          <div className={`parallel-col bible-text-${langA}`}>
            {leftLine !== null ? formatVerseLine(leftLine, 0, noteA, parsedA[i], langA, langA, ownersA[i]) : <span className="parallel-unavailable">—</span>}
          </div>
          <div className={`parallel-col bible-text-${langB}`}>
            {rightLine !== null ? formatVerseLine(rightLine, 0, noteB, parsedB[i], langB, langB, ownersB[i]) : <span className="parallel-unavailable">—</span>}
          </div>
        </div>
      );
    });
  }

  const ABBREV = {
    'Gen.':'Genesis','Ex.':'Exodus','Exo.':'Exodus','Exod.':'Exodus','Lev.':'Leviticus',
    'Num.':'Numbers','Deut.':'Deuteronomy','Josh.':'Joshua','Judg.':'Judges',
    '1 Sam.':'1 Samuel','2 Sam.':'2 Samuel',
    '1 Sam':'1 Samuel','2 Sam':'2 Samuel',
    '1 Kgs.':'1 Kings','2 Kgs.':'2 Kings',
    '1 Chr.':'1 Chronicles','2 Chr.':'2 Chronicles',
    '1 Chron.':'1 Chronicles','2 Chron.':'2 Chronicles',
    '1 King':'1 Kings','2 King':'2 Kings',
    'Neh.':'Nehemiah','Esth.':'Esther',
    'Psa.':'Psalms','Ps.':'Psalms','Pss.':'Psalms','Prov.':'Proverbs',
    'Eccl.':'Ecclesiastes','Song.':'Song of Songs','Song of Sol.':'Song of Songs',
    'Isa.':'Isaiah','Jer.':'Jeremiah','Lam.':'Lamentations',
    'Ezek.':'Ezekiel','Dan.':'Daniel','Hos.':'Hosea',
    'Mic.':'Micah','Nah.':'Nahum','Hab.':'Habakkuk','Zeph.':'Zephaniah',
    'Hag.':'Haggai','Zech.':'Zechariah','Mal.':'Malachi',
    'Matt.':'Matthew','Mk.':'Mark','Lk.':'Luke','Jn.':'John',
    'Rom.':'Romans',
    '1 Cor.':'1 Corinthians','2 Cor.':'2 Corinthians',
    'Gal.':'Galatians','Eph.':'Ephesians','Phil.':'Philippians','Col.':'Colossians',
    '1 Thess.':'1 Thessalonians','2 Thess.':'2 Thessalonians',
    '1 Tim.':'1 Timothy','2 Tim.':'2 Timothy',
    'Philem.':'Philemon','Heb.':'Hebrews','Jas.':'James',
    '1 Pet.':'1 Peter','2 Pet.':'2 Peter',
    '1 Jn.':'1 John','2 Jn.':'2 John','3 Jn.':'3 John','Rev.':'Revelation',
  };

  const ZH_MAP = {
    'Matthew':'太','Mark':'可','Luke':'路','John':'約',
    'Acts':'徒','Romans':'羅','Genesis':'創','Exodus':'出',
    'Leviticus':'利','Numbers':'民','Deuteronomy':'申',
    'Psalms':'詩','Proverbs':'箴','Isaiah':'賽','Revelation':'啟',
    '1 Samuel':'撒上','2 Samuel':'撒下','1 Kings':'王上','2 Kings':'王下',
    '1 Chronicles':'代上','2 Chronicles':'代下',
    'Jeremiah':'耶','Ezekiel':'結','Daniel':'但',
    'Joshua':'書','Judges':'士','Ruth':'得','Ezra':'拉',
    'Nehemiah':'尼','Esther':'斯','Job':'伯',
    'Ecclesiastes':'傳','Song of Songs':'歌',
    'Lamentations':'哀','Hosea':'何','Joel':'珥','Amos':'摩',
    'Obadiah':'俄','Jonah':'拿','Micah':'彌','Nahum':'鴻',
    'Habakkuk':'哈','Zephaniah':'番','Haggai':'該',
    'Zechariah':'亞','Malachi':'瑪','Hebrews':'來','James':'雅',
    '1 John':'約一','2 John':'約二','3 John':'約三',
    '1 Peter':'彼前','2 Peter':'彼後','Jude':'猶',
    '1 Corinthians':'林前','2 Corinthians':'林後',
    'Galatians':'加','Ephesians':'弗','Philippians':'腓',
    'Colossians':'西','1 Thessalonians':'帖前','2 Thessalonians':'帖後',
    '1 Timothy':'提前','2 Timothy':'提後','Titus':'多','Philemon':'門',
  };

  const SC_MAP = {
    'Matthew':'太','Mark':'可','Luke':'路','John':'约',
    'Acts':'徒','Romans':'罗','Genesis':'创','Exodus':'出',
    'Leviticus':'利','Numbers':'民','Deuteronomy':'申',
    'Psalms':'诗','Proverbs':'箴','Isaiah':'赛','Revelation':'启',
    '1 Samuel':'撒上','2 Samuel':'撒下','1 Kings':'王上','2 Kings':'王下',
    '1 Chronicles':'代上','2 Chronicles':'代下',
    'Jeremiah':'耶','Ezekiel':'结','Daniel':'但',
    'Joshua':'书','Judges':'士','Ruth':'得','Ezra':'拉',
    'Nehemiah':'尼','Esther':'斯','Job':'伯',
    'Ecclesiastes':'传','Song of Songs':'歌',
    'Lamentations':'哀','Hosea':'何','Joel':'珥','Amos':'摩',
    'Obadiah':'俄','Jonah':'拿','Micah':'弥','Nahum':'鸿',
    'Habakkuk':'哈','Zephaniah':'番','Haggai':'该',
    'Zechariah':'亚','Malachi':'玛','Hebrews':'来','James':'雅',
    '1 John':'约一','2 John':'约二','3 John':'约三',
    '1 Peter':'彼前','2 Peter':'彼后','Jude':'犹',
    '1 Corinthians':'林前','2 Corinthians':'林后',
    'Galatians':'加','Ephesians':'弗','Philippians':'腓',
    'Colossians':'西','1 Thessalonians':'帖前','2 Thessalonians':'帖后',
    '1 Timothy':'提前','2 Timothy':'提后','Titus':'多','Philemon':'门',
  };

  const ES_MAP = {
    'Matthew':'Mt.','Mark':'Mc.','Luke':'Lc.','John':'Jn.',
    'Acts':'Hch.','Romans':'Ro.','Genesis':'Gn.','Exodus':'Éx.',
    'Leviticus':'Lv.','Numbers':'Nm.','Deuteronomy':'Dt.',
    'Psalms':'Sal.','Proverbs':'Pr.','Isaiah':'Is.','Revelation':'Ap.',
    '1 Samuel':'1 S.','2 Samuel':'2 S.','1 Kings':'1 R.','2 Kings':'2 R.',
    '1 Chronicles':'1 Cr.','2 Chronicles':'2 Cr.',
    'Jeremiah':'Jer.','Ezekiel':'Ez.','Daniel':'Dn.',
    'Joshua':'Jos.','Judges':'Jue.','Ruth':'Rut',
    'Job':'Job','Hebrews':'Heb.','James':'Sant.',
    '1 Corinthians':'1 Co.','2 Corinthians':'2 Co.',
    'Galatians':'Gá.','Ephesians':'Ef.','Philippians':'Fil.',
    'Colossians':'Col.','1 John':'1 Jn.','2 John':'2 Jn.','3 John':'3 Jn.',
    '1 Peter':'1 Pe.','2 Peter':'2 Pe.','Jude':'Jud.',
  };

  function normalizeBook(abbrev) {
    return ABBREV[abbrev] || abbrev;
  }

  function translatePortion(portion, language) {
    if (!portion || language === 'en') return portion;
    let result = portion;
    Object.entries(ABBREV).sort((a,b) => b[0].length - a[0].length).forEach(([abbr, full]) => {
      const escaped = abbr.replace(/\./g, '\\.');
      const endsWithWordChar = /\w/.test(abbr[abbr.length - 1]);
      const pattern = '\\b' + escaped + (endsWithWordChar ? '\\b' : '');
      result = result.replace(new RegExp(pattern, 'g'), full);
    });
    if (language === 'zh' || language === 'sc') {
      const cmap = language === 'sc' ? SC_MAP : ZH_MAP;
      Object.entries(cmap).forEach(([full, char]) => {
        result = result.replace(new RegExp(full, 'g'), char);
      });
    } else if (language === 'es') {
      Object.entries(ES_MAP).forEach(([full, abbr]) => {
        result = result.replace(new RegExp(full, 'g'), abbr);
      });
    }
    return result;
  }

  function getBookLabel(title, language) {
    if (!title) return '';
    const clean = title
      .replace(/New Testament\s*-\s*/i, '')
      .replace(/Old Testament\s*-\s*/i, '')
      .replace(/\s+[\d:~]+.*/, '').trim();
    const full = normalizeBook(clean);
    if (language === 'en') return full;
    if (language === 'es') return ES_MAP[full] || full;
    if (language === 'zh') return ZH_MAP[full] || full;
    if (language === 'sc') return SC_MAP[full] || full;
    return full;
  }

  function getCrossBookChapterLabels(title, audioCount, language) {
    if (!title || !audioCount) return null;
    const clean = title.replace(/^(New|Old) Testament\s*-\s*/i, '').trim();
    const m = clean.match(/^(.+?)\s+(\d+):\d+\s*[~-]\s*(.+?)\s+(\d+):\d+\s*$/);
    if (!m) return null;
    const startBookFull = normalizeBook(m[1].trim());
    const startChap    = parseInt(m[2]);
    const endBookFull  = normalizeBook(m[3].trim());
    const endChap      = parseInt(m[4]);
    if (startBookFull === endBookFull) return null;
    const book2Count = endChap;
    const book1Count = audioCount - book2Count;
    function bookL(fullName) {
      if (language === 'zh') return ZH_MAP[fullName] || fullName;
      if (language === 'sc') return SC_MAP[fullName] || fullName;
      if (language === 'es') return ES_MAP[fullName] || fullName;
      return fullName;
    }
    function chapLbl(b, n) {
      if (language === 'zh' || language === 'sc') return b + ' \u7b2c' + n + '\u7ae0';
      if (language === 'es') return b + ' Cap. ' + n;
      return b + ' Ch. ' + n;
    }
    const labels = [];
    const b1 = bookL(startBookFull);
    for (let i = 0; i < book1Count; i++) labels.push(chapLbl(b1, startChap + i));
    const b2 = bookL(endBookFull);
    for (let i = 1; i <= book2Count; i++) labels.push(chapLbl(b2, i));
    return labels;
  }

  const t = pick(readingText, lang);

  function getAudioForLang(langCode, portion) {
    if (!verses) return null;
    const zhAudio = verses[`${portion}_audio_zh`];
    const enAudio = verses[`${portion}_audio`];
    if ((langCode === 'zh' || langCode === 'sc') && zhAudio) return zhAudio;
    if (langCode === 'es') {
      const es = esAudio[portion];
      if (es && es.bounds === (portion === 'nt' ? ntBounds : otBounds)) return es.json;
    }
    return enAudio || null;
  }

  // ── Verse timings for the recordings ─────────────────────────────────────
  // Where each portion starts and ends, so the players can begin at its first
  // verse and stop after its last one once timings exist for the recording.
  const ntBounds = React.useMemo(() => portionBounds(verses?.nt_title), [verses?.nt_title]);
  const otBounds = React.useMemo(() => portionBounds(verses?.ot_title), [verses?.ot_title]);
  const audioTimingLang = l => timingLangFor(l);

  // Spanish recordings are stored per chapter in bible_chapters.audio_es
  // (narrated Gospels, generated audio elsewhere); the day's verses row has
  // only English and Chinese. Each portion's chapters are fetched and joined
  // into the same JSON list the players take. Until they arrive, or if any
  // chapter lacks one, Spanish falls back to the English recording as before.
  const wantsEs = lang === 'es' || (parallelMode && (parallelLangA === 'es' || parallelLangB === 'es'));
  const [esAudio, setEsAudio] = React.useState({});
  React.useEffect(() => {
    if (!wantsEs) return;
    let cancelled = false;
    for (const [portion, b] of [['nt', ntBounds], ['ot', otBounds]]) {
      if (!b) continue;
      const { first, last } = b;
      const filter = first.book === last.book
        ? `and(book_abbr.eq.${first.book},chapter.gte.${first.chapter},chapter.lte.${last.chapter})`
        : `and(book_abbr.eq.${first.book},chapter.gte.${first.chapter}),and(book_abbr.eq.${last.book},chapter.lte.${last.chapter})`;
      supabase.from('bible_chapters').select('book_abbr,chapter,audio_es').or(filter).then(({ data }) => {
        if (cancelled || !data?.length) return;
        const rank = r => (r.book_abbr === first.book ? 0 : 1);
        const urls = [...data].sort((x, y) => rank(x) - rank(y) || x.chapter - y.chapter).map(r => r.audio_es);
        const json = urls.every(Boolean) ? JSON.stringify(urls) : null;
        setEsAudio(prev => ({ ...prev, [portion]: { bounds: b, json } }));
      });
    }
    return () => { cancelled = true; };
  }, [wantsEs, ntBounds, otBounds]);

  // The player that can play the first selected verse: the NT or OT one, and
  // in parallel mode the one in the selected column's language. Null when that
  // recording has no timing for the verse (then no Listen button shows).
  function listenTarget(ranges) {
    if (!ranges?.length) return null;
    const first = [...ranges].sort(compareVerses)[0];
    const portion = NT_SET.has(first.book) ? 'nt' : 'ot';
    const player = playersRef.current[`${portion}:${parallelMode ? first.lang : lang}`];
    return player?.canPlayFrom(first.book, first.chapter, first.verse) ? { player, first } : null;
  }
  const canListen = ranges => !!listenTarget(ranges);
  function listenFrom(ranges) {
    const target = listenTarget(ranges);
    if (!target) return;
    const { player, first } = target;
    player.playFrom(first.book, first.chapter, first.verse);
    window.getSelection?.()?.removeAllRanges();
  }

  // ── Turning days like pages (src/pageturn/) ──────────────────────────────
  // The day's cards, readers and discussion are the page; the date bar,
  // controls and Daily Bread above stay put. ← / → and a sideways swipe turn
  // to the day before or after, and so do the date bar's arrows.
  const linePreview = (text, title) => {
    if (!text) return null;
    const lines = text.split('\n');
    const parsed = parseReadingLines(text, title);
    return lines.slice(0, 14).map((line, i) => {
      if (!line.trim()) return null;
      const p = parsed[i];
      return (
        <p key={i} className="verse-line" style={{ fontSize: fontSize + 'px' }}>
          {p ? <><span className="verse-ref">{p.prefix}</span><span className="verse-body"> {p.body}</span></> : line}
        </p>
      );
    });
  };
  // The day beside this one as it slides in: its portions, and the first
  // verses of each when that day's row has been fetched ahead.
  const renderDayPreview = dir => {
    const date = shiftDate(currentDate, dir);
    const row = dayRows.get(queryDateOf(date)) || null;
    const plan = getReadingForDate(date);
    // The real cards hold the audio player and the show/hide button between
    // the heading and the verses; leave the same room so the text lines up.
    const card = document.querySelector('.reading-cards .reading-card');
    const head = card?.querySelector('.reading-card-header');
    const box = card?.querySelector('.verses-box');
    const gap = head && box ? Math.max(0, box.getBoundingClientRect().top - head.getBoundingClientRect().bottom) : 0;
    const card1 = (portion, tag) => {
      const title = row?.[`${portion}_title`];
      return (
        <div className="reading-card" key={portion}>
          <div className="reading-card-header">
            <span className={`reading-tag ${portion}-tag`}>{tag}</span>
            <span className="reading-portion">{translatePortion(plan?.[portion], lang)}</span>
          </div>
          {gap > 0 && <div style={{ height: gap }} />}
          <div className={`verses-box${parallelMode ? ' verses-box-parallel' : ''}`}>
            {!row ? <SkeletonLines count={8} /> : parallelMode ? (
              <div className="parallel-row">
                <div className={`parallel-col bible-text-${parallelLangA}`}>
                  <div className="verses-title">{getTitle(title, parallelLangA)}</div>
                  {linePreview(getTextForLang(parallelLangA, portion, false, row), title)}
                </div>
                <div className={`parallel-col bible-text-${parallelLangB}`}>
                  <div className="verses-title">{getTitle(title, parallelLangB)}</div>
                  {linePreview(getTextForLang(parallelLangB, portion, false, row), title)}
                </div>
              </div>
            ) : (
              <>
                <div className="verses-title">{getTitle(title)}</div>
                <div className={`verses-text bible-text-${lang}`}>{linePreview(getTextForLang(lang, portion, true, row), title)}</div>
              </>
            )}
          </div>
        </div>
      );
    };
    return (
      <div className="pt-preview">
        <div className="pt-badge">{dayLabel(date, { weekday: 'short', month: 'short', day: 'numeric' })}</div>
        <div className={`reading-cards${parallelMode ? ' reading-cards-parallel' : ''}`}>
          {card1('nt', 'NT')}
          {card1('ot', 'OT')}
        </div>
      </div>
    );
  };

  const turner = usePageTurn({
    canPrev: true,
    canNext: true,
    onTurn: (dir, _target, { step = 0 } = {}) => {
      changeDate(dir, step > 0);
      // A held arrow key gives the usual fetch-ahead no pause to run in.
      if (step > 0) fetchDayRow(queryDateOf(shiftDate(currentDate, dir * 2)));
    },
    renderNeighbour: renderDayPreview,
    pageKey: currentDate,
    ready: versesDate === currentDate,
    getTopInset: readingTopInset,
  });

  // Fetch the days either side once this one is in, so a turn slides in text.
  React.useEffect(() => {
    if (versesDate !== currentDate) return;
    const id = setTimeout(() => {
      fetchDayRow(queryDateOf(shiftDate(currentDate, 1)));
      fetchDayRow(queryDateOf(shiftDate(currentDate, -1)));
    }, 250);
    return () => clearTimeout(id);
  }, [versesDate, currentDate]);

  const bannerContent = [...bannerItems, ...bannerItems];
  const dateShort = currentDate.slice(5).replace('-', '/');

  return (
    <PopupContext.Provider value={popups.popupContextValue}>
      <RefPopupLayer popups={popups} />
      <AnnotationLayer rootRef={annoRootRef} lang={lang} onListen={listenFrom} canListen={canListen} />
      <FollowAudioPill visible={follow.pillVisible} onClick={follow.resume} label={ta.followAudio} />
      {turner.layer}
      {bannerItems.length > 0 && (
        <div className="sticky-banner" style={{ top: navbarHeight }}>
          <div className="banner-track">
            {bannerContent.map((r, i) => (
              <span key={i} className="banner-item">
                <span className={r.portion === 'NT' ? 'banner-nt' : 'banner-ot'}>{r.name}</span>
                {' '}finished {r.portion} ({dateShort}) {r.time}
                <span className="banner-sep"> • </span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="page reading-page-content">
        <div className="date-nav">
          <button className="nav-arrow" onClick={() => turner.turn(-1)} aria-label={t.prevDay} title={t.prevDay}>←</button>
          <div className="date-nav-center">
            <div className="reading-date-label">{dateLabel}</div>
            <div className="reading-day-count">{t.dayCount(dayOfYear, daysInYear)}</div>
          </div>
          <button className="nav-arrow" onClick={() => turner.turn(1)} aria-label={t.nextDay} title={t.nextDay}>→</button>
        </div>

        <div className="reading-sticky-controls" style={{ top: controlsTop }}>
        <div className="reading-meta-row">
          <span className="readers-count-pill">
            {todayReaders.length} {todayReaders.length === 1 ? t.person : t.people} {t.today}
          </span>
          <div className="font-size-row">
            <span>{t.text}</span>
            <button className="font-btn" onClick={() => setFontSize(f => Math.max(12, f - 2))}>A−</button>
            <button className="font-btn" onClick={() => setFontSize(18)}>A</button>
            <button className="font-btn" onClick={() => setFontSize(f => Math.min(26, f + 2))}>A+</button>
          </div>
          <div className="reading-meta-actions">
          <button
            className={`parallel-btn${parallelMode ? ' active' : ''}`}
            onClick={() => {
              const next = !parallelMode;
              setParallelMode(next);
              localStorage.setItem('parallelMode', next);
              if (next) {
                setParallelLangA(lang);
                localStorage.setItem('parallelLangA', lang);
                // Ensure B is different from A
                if (parallelLangB === lang) {
                  const newB = lang === 'zh' || lang === 'sc' || lang === 'es' ? 'en' : 'zh';
                  setParallelLangB(newB);
                  localStorage.setItem('parallelLangB', newB);
                }
              }
            }}
          >
            ⇔ {lang === 'zh' ? '對照' : lang === 'sc' ? '对照' : lang === 'es' ? 'Paralelo' : 'Parallel'}
          </button>
          {canSeeRefs && (
            <button
              className={`parallel-btn${showRefs ? ' active' : ''}`}
              title="Toggle cross-references and footnotes"
              onClick={() => { setShowRefs(r => !r); popups.closeAllPopups(); }}
            >
              {showRefs ? '✓ ' : ''}📖 Refs
            </button>
          )}
          </div>
        </div>

        {parallelMode && (
          <div className="parallel-lang-bar">
            <LangSelect
              label="Left column language"
              value={parallelLangA}
              onChange={code => { setParallelLangA(code); localStorage.setItem('parallelLangA', code); }}
            />
            <span className="parallel-sep">⇔</span>
            <LangSelect
              label="Right column language"
              value={parallelLangB}
              onChange={code => { setParallelLangB(code); localStorage.setItem('parallelLangB', code); }}
            />
          </div>
        )}
        </div>

        <DailyBread date={currentDate} lang={lang} fontSize={fontSize} stickyTop={dailyBreadTop} />

        <TurnPage turner={turner}>

        <div ref={annoRootRef} className={`reading-cards${parallelMode ? ' reading-cards-parallel' : ''}`}>
          {/* NT Card */}
          <div className={`reading-card ${ntDone ? 'done' : ''}`}>
            <div className="reading-card-header">
              <span className="reading-tag nt-tag">NT</span>
              <span className="reading-portion">{translatePortion(reading.nt, lang)}</span>
            </div>
            {parallelMode ? (
              <div className="parallel-audio-row">
                {getAudioForLang(parallelLangA, 'nt') && (
                  <AudioPlayer key={`nt-A-${parallelLangA}`}
                    ref={playerRef(`nt:${parallelLangA}`)} {...followProps(`nt:${parallelLangA}`)} bounds={ntBounds} onTimings={onTimings} timingLang={audioTimingLang(parallelLangA)}
                    label={parallelLangA === 'zh' ? '新約音頻' : parallelLangA === 'sc' ? '新约音频' : parallelLangA === 'es' ? 'Audio NT' : 'NT Audio'}
                    book={getBookLabel(verses.nt_title, parallelLangA)}
                    audioJson={getAudioForLang(parallelLangA, 'nt')}
                    startChap={parseInt(verses.nt_title?.match(/(\d+):/)?.[1] || '1', 10)}
                    lang={parallelLangA}
                    chapterLabels={getCrossBookChapterLabels(verses.nt_title, JSON.parse(getAudioForLang(parallelLangA, 'nt') || '[]').length, parallelLangA)}
                    onFinished={() => handleAudioFinished('NT')}
                  />
                )}
                {getAudioForLang(parallelLangB, 'nt') && (
                  <AudioPlayer key={`nt-B-${parallelLangB}`}
                    ref={playerRef(`nt:${parallelLangB}`)} {...followProps(`nt:${parallelLangB}`)} bounds={ntBounds} onTimings={onTimings} timingLang={audioTimingLang(parallelLangB)}
                    label={parallelLangB === 'zh' ? '新約音頻' : parallelLangB === 'sc' ? '新约音频' : parallelLangB === 'es' ? 'Audio NT' : 'NT Audio'}
                    book={getBookLabel(verses.nt_title, parallelLangB)}
                    audioJson={getAudioForLang(parallelLangB, 'nt')}
                    startChap={parseInt(verses.nt_title?.match(/(\d+):/)?.[1] || '1', 10)}
                    lang={parallelLangB}
                    chapterLabels={getCrossBookChapterLabels(verses.nt_title, JSON.parse(getAudioForLang(parallelLangB, 'nt') || '[]').length, parallelLangB)}
                    onFinished={() => handleAudioFinished('NT')}
                  />
                )}
              </div>
            ) : getNtAudio() && (
              <AudioPlayer
                key={`nt-${lang}`}
                ref={playerRef(`nt:${lang}`)} {...followProps(`nt:${lang}`)} bounds={ntBounds} onTimings={onTimings} timingLang={audioTimingLang(lang)}
                label={lang === 'zh' ? '新約音頻' : lang === 'sc' ? '新约音频' : lang === 'es' ? 'Audio NT' : 'NT Audio'}
                book={getBookLabel(verses.nt_title, lang)}
                audioJson={getNtAudio()}
                startChap={parseInt(verses.nt_title?.match(/(\d+):/)?.[1] || '1', 10)}
                lang={lang}
                chapterLabels={getCrossBookChapterLabels(verses.nt_title, JSON.parse(getNtAudio() || '[]').length, lang)}
                onFinished={() => handleAudioFinished('NT')}
              />
            )}
            <button className="verses-toggle" onClick={() => setShowNT(!showNT)}>
              {showNT ? t.hideVerses : t.readVerses}
            </button>
            {showNT && (
              <div className={`verses-box${parallelMode ? ' verses-box-parallel' : ''}`} onClick={refsOn ? popups.closeAllPopups : undefined}>
                {verses ? parallelMode ? (
                  <>
                    <div className="parallel-titles-row">
                      <div className="verses-title">{getTitle(verses.nt_title, parallelLangA)}</div>
                      <div className="verses-title">{getTitle(verses.nt_title, parallelLangB)}</div>
                    </div>
                    <div className="verses-text">
                      {formatVersesParallel(getTextForLang(parallelLangA, 'nt', false), getTextForLang(parallelLangB, 'nt', false), parallelLangA, parallelLangB,
                        annotateLines(getTextForLang(parallelLangA, 'nt', false), verses.nt_title, parallelLangA),
                        annotateLines(getTextForLang(parallelLangB, 'nt', false), verses.nt_title, parallelLangB),
                        verses.nt_title)}
                    </div>
                  </>
                ) : (
                  <><div className="verses-title">{getTitle(verses.nt_title)}</div>
                  <div className={`verses-text bible-text-${lang}`}>{formatVerses(getNtText(), annotateLines(getNtText(), verses.nt_title, textLangOf(lang, 'nt')), verses.nt_title, textLangOf(lang, 'nt'))}</div></>
                ) : <div className="loading">Loading...</div>}
              </div>
            )}
            <button className={`checkin-btn ${ntDone ? 'checked' : ''}`}
              onClick={() => handleCheckin('NT', ntDone, setNtDone)} disabled={ntDone || !!saving}>
              {saving === 'NT' ? t.saving : ntDone ? t.finishedNT : t.finishNT}
            </button>
          </div>

          {/* OT Card */}
          <div className={`reading-card ${otDone ? 'done' : ''}`}>
            <div className="reading-card-header">
              <span className="reading-tag ot-tag">OT</span>
              <span className="reading-portion">{translatePortion(reading.ot, lang)}</span>
            </div>
            {parallelMode ? (
              <div className="parallel-audio-row">
                {getAudioForLang(parallelLangA, 'ot') && (
                  <AudioPlayer key={`ot-${parallelLangA}`}
                    ref={playerRef(`ot:${parallelLangA}`)} {...followProps(`ot:${parallelLangA}`)} bounds={otBounds} onTimings={onTimings} timingLang={audioTimingLang(parallelLangA)}
                    label={parallelLangA === 'zh' ? '舊約音頻' : parallelLangA === 'sc' ? '旧约音频' : parallelLangA === 'es' ? 'Audio AT' : 'OT Audio'}
                    book={getBookLabel(verses.ot_title, parallelLangA)}
                    audioJson={getAudioForLang(parallelLangA, 'ot')}
                    startChap={parseInt(verses.ot_title?.match(/(\d+):/)?.[1] || '1', 10)}
                    lang={parallelLangA}
                    chapterLabels={getCrossBookChapterLabels(verses.ot_title, JSON.parse(getAudioForLang(parallelLangA, 'ot') || '[]').length, parallelLangA)}
                    onFinished={() => handleAudioFinished('OT')}
                  />
                )}
                {getAudioForLang(parallelLangB, 'ot') && (
                  <AudioPlayer key={`ot-${parallelLangB}`}
                    ref={playerRef(`ot:${parallelLangB}`)} {...followProps(`ot:${parallelLangB}`)} bounds={otBounds} onTimings={onTimings} timingLang={audioTimingLang(parallelLangB)}
                    label={parallelLangB === 'zh' ? '舊約音頻' : parallelLangB === 'sc' ? '旧约音频' : parallelLangB === 'es' ? 'Audio AT' : 'OT Audio'}
                    book={getBookLabel(verses.ot_title, parallelLangB)}
                    audioJson={getAudioForLang(parallelLangB, 'ot')}
                    startChap={parseInt(verses.ot_title?.match(/(\d+):/)?.[1] || '1', 10)}
                    lang={parallelLangB}
                    chapterLabels={getCrossBookChapterLabels(verses.ot_title, JSON.parse(getAudioForLang(parallelLangB, 'ot') || '[]').length, parallelLangB)}
                    onFinished={() => handleAudioFinished('OT')}
                  />
                )}
              </div>
            ) : getOtAudio() && (
              <AudioPlayer
                key={`ot-${lang}`}
                ref={playerRef(`ot:${lang}`)} {...followProps(`ot:${lang}`)} bounds={otBounds} onTimings={onTimings} timingLang={audioTimingLang(lang)}
                label={lang === 'zh' ? '舊約音頻' : lang === 'sc' ? '旧约音频' : lang === 'es' ? 'Audio AT' : 'OT Audio'}
                book={getBookLabel(verses.ot_title, lang)}
                audioJson={getOtAudio()}
                startChap={parseInt(verses.ot_title?.match(/(\d+):/)?.[1] || '1', 10)}
                lang={lang}
                chapterLabels={getCrossBookChapterLabels(verses.ot_title, JSON.parse(getOtAudio() || '[]').length, lang)}
                onFinished={() => handleAudioFinished('OT')}
              />
            )}
            <button className="verses-toggle" onClick={() => setShowOT(!showOT)}>
              {showOT ? t.hideVerses : t.readVerses}
            </button>
            {showOT && (
              <div className={`verses-box${parallelMode ? ' verses-box-parallel' : ''}`} onClick={refsOn ? popups.closeAllPopups : undefined}>
                {verses ? parallelMode ? (
                  <>
                    <div className="parallel-titles-row">
                      <div className="verses-title">{getTitle(verses.ot_title, parallelLangA)}</div>
                      <div className="verses-title">{getTitle(verses.ot_title, parallelLangB)}</div>
                    </div>
                    <div className="verses-text">
                      {formatVersesParallel(getTextForLang(parallelLangA, 'ot', false), getTextForLang(parallelLangB, 'ot', false), parallelLangA, parallelLangB,
                        annotateLines(getTextForLang(parallelLangA, 'ot', false), verses.ot_title, parallelLangA),
                        annotateLines(getTextForLang(parallelLangB, 'ot', false), verses.ot_title, parallelLangB),
                        verses.ot_title)}
                    </div>
                  </>
                ) : (
                  <><div className="verses-title">{getTitle(verses.ot_title)}</div>
                  <div className={`verses-text bible-text-${lang}`}>{formatVerses(getOtText(), annotateLines(getOtText(), verses.ot_title, textLangOf(lang, 'ot')), verses.ot_title, textLangOf(lang, 'ot'))}</div></>
                ) : <div className="loading">Loading...</div>}
              </div>
            )}
            <button className={`checkin-btn ot ${otDone ? 'checked' : ''}`}
              onClick={() => handleCheckin('OT', otDone, setOtDone)} disabled={otDone || !!saving}>
              {saving === 'OT' ? t.saving : otDone ? t.finishedOT : t.finishOT}
            </button>
          </div>
        </div>

        {ntDone && otDone && <div className="completed-banner">{t.completed}</div>}

        {todayReaders.length > 0 && (
          <div className="readers-section">
            <div className="readers-title">{t.readersTitle} ({todayReaders.length})</div>
            <div className="readers-table">
              <div className="readers-header">
                <span>{lang === 'en' ? 'Name' : lang === 'es' ? 'Nombre' : '姓名'}</span>
                <span>NT</span><span>OT</span>
              </div>
              {todayReaders.map((r, i) => (
                <div className="readers-row" key={`${r.name}-${i}`}>
                  <span>{r.name}</span>
                  <span>{r.nt ? <span className="check-nt">✓</span> : '—'}</span>
                  <span>{r.ot ? <span className="check-ot">✓</span> : '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <CommentsSection queryDate={commentDate} lang={lang} />
        </TurnPage>

        {/* Outside the turning page: it is pinned to the screen, and a moving
            page would carry it along. */}
        {toast && <div className="audio-toast" role="status">{toast}</div>}
      </div>
    </PopupContext.Provider>
  );
}